import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Divider,
  message,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  SaveOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { UserContext } from "../../../context/UserContext";
import {
  getOrgs,
  getSites,
  getCrops,
  getGreenhouses,
} from "../../../utils/api";
import {
  getPlotDataVisibility,
  updatePlotDataVisibility,
  getHarvestDates,
} from "../../../utils/api";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../consts/index.js";
import { LoadingIcon } from "../../../components/index.js";

/* ---------- helpers ---------- */
const numericPrefix = (label) => {
  if (!label) return { hasNumber: false, num: Infinity, hasSuffix: false };
  const m = String(label)
    .trim()
    .match(/^(\d+)/);
  if (!m) return { hasNumber: false, num: Infinity, hasSuffix: false };
  const num = parseInt(m[1], 10);
  const rest = String(label).trim().slice(m[1].length).trim();
  return { hasNumber: true, num, hasSuffix: rest.length > 0 };
};

const sortGreenhousesSmart = (items) => {
  const parsed = items.map((it) => ({
    ...it,
    _i: numericPrefix(it.name || it.label),
  }));
  const allNumeric = parsed.every((p) => p._i.hasNumber);
  if (!allNumeric) {
    return items
      .slice()
      .sort((a, b) =>
        String(a.name || a.label).localeCompare(String(b.name || b.label)),
      );
  }
  return parsed
    .sort((a, b) => {
      if (a._i.num !== b._i.num) return a._i.num - b._i.num;
      if (a._i.hasSuffix !== b._i.hasSuffix) return a._i.hasSuffix ? 1 : -1;
      return String(a.name || a.label).localeCompare(String(b.name || b.label));
    })
    .map(({ _i, ...rest }) => rest);
};

const resolveGreenhouseId = (r) =>
  r.greenhouse_id ??
  (typeof r.greenhouse === "string" ? r.greenhouse : r.greenhouse?.id) ??
  null;

const resolveGreenhouseName = (r, ghMap) =>
  r.greenhouse_name ??
  (typeof r.greenhouse === "object" && r.greenhouse?.name) ??
  (ghMap && ghMap.get?.(resolveGreenhouseId(r))) ??
  "—";

const resolveHarvestDate = (r) =>
  r.harvest_date ?? r.harvest?.date ?? r.harvests?.date ?? null;

const Ellip = ({
  text,
  maxW = "max-w-[240px] md:max-w-[180px] lg:max-w-[140px]",
}) => (
  <Tooltip title={text}>
    <span className={`block truncate ${maxW}`}>{text ?? "—"}</span>
  </Tooltip>
);

const MSG_KEYS = {
  refresh: "plotdata_refresh",
  save: "plotdata_save",
};

const sortRowsDefault = (arr) =>
  arr.slice().sort((a, b) => {
    const da = a.harvest_date || "";
    const db = b.harvest_date || "";
    if (da !== db) return db.localeCompare(da); // Desc by date (YYYY-MM-DD safe)
    const gha = String(a.greenhouse_name || "");
    const ghb = String(b.greenhouse_name || "");
    const gcmp = gha.localeCompare(ghb);
    if (gcmp) return gcmp; // Asc greenhouse
    return String(a.plot_name || "").localeCompare(String(b.plot_name || "")); // Asc plot
  });

const sortByNameAsc = (items) =>
  items
    .slice()
    .sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      }),
    );

/* ---------- component ---------- */
const PlotDataVisibilityTable = () => {
  const { userData } = useContext(UserContext);

  const [msg, contextHolder] = message.useMessage();

  // filters (org/site/crop)
  const [orgs, setOrgs] = useState([]);
  const [sites, setSites] = useState([]);
  const [crops, setCrops] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [siteId, setSiteId] = useState(null);
  const [cropId, setCropId] = useState(null);

  const [plotHeaderLabel, setPlotHeaderLabel] = useState("Plot");
  const [greenhouseHeaderLabel, setGreenhouseHeaderLabel] =
    useState("Greenhouse");

  // greenhouses
  const [ghMap, setGhMap] = useState(new Map());
  const [ghOptions, setGhOptions] = useState([]);
  const [selectedGreenhouses, setSelectedGreenhouses] = useState([]);

  // harvest dates (allowed + selected)
  const [allowedDates, setAllowedDates] = useState([]); // ["YYYY-MM-DD"]
  const allowedSet = useMemo(() => new Set(allowedDates), [allowedDates]);
  const [dates, setDates] = useState([]);
  const datePickerValue = useMemo(
    () => (dates.length ? dayjs(dates[dates.length - 1], "YYYY-MM-DD") : null),
    [dates],
  );

  // remember selection before “Show all hidden”
  const [prevSelection, setPrevSelection] = useState(null);

  // table
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [dirty, setDirty] = useState({});
  const dirtyCount = useMemo(() => Object.keys(dirty).length, [dirty]);

  // hidden-only view
  const [showHiddenOnly, setShowHiddenOnly] = useState(false);

  const resetRows = () => {
    setRows([]);
    setSelectedRowKeys([]);
    setDirty({});
  };
  const resetForOrg = (newOrgId) => {
    setOrgId(newOrgId);
    setSites([]);
    setSiteId(null);
    setCrops([]);
    setCropId(null);
    setGhMap(new Map());
    setGhOptions([]);
    setSelectedGreenhouses([]);
    setAllowedDates([]);
    setDates([]);
    setShowHiddenOnly(false);
    setPrevSelection(null);
    resetRows();
  };
  const resetForSite = (newSiteId) => {
    setSiteId(newSiteId);
    setCrops([]);
    setCropId(null);
    setGhMap(new Map());
    setGhOptions([]);
    setSelectedGreenhouses([]);
    setAllowedDates([]);
    setDates([]);
    setShowHiddenOnly(false);
    setPrevSelection(null);
    resetRows();
  };
  const resetForCrop = (newCropId) => {
    setCropId(newCropId);
    setGhMap(new Map());
    setGhOptions([]);
    setSelectedGreenhouses([]);
    setAllowedDates([]);
    setDates([]);
    setShowHiddenOnly(false);
    setPrevSelection(null);
    resetRows();
  };

  useEffect(() => {
    (async () => {
      if (!userData?.is_superuser) return;
      try {
        const res = await getOrgs();
        const list = sortByNameAsc(
          (res?.data?.data || []).map((it) => ({
            id: it.id || it.key || it?.key,
            name: it.name,
          })),
        );
        setOrgs(list);
        if (list[0]) setOrgId(list[0].id);
      } catch {
        msg.error("Failed to load organizations");
      }
    })();
  }, [userData, msg]);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const res = await getSites(orgId);
        const list = sortByNameAsc(
          (res?.data?.data || []).map((it) => ({
            id: it.id,
            name: it.display_name || it.name,
            metadata: it.metadata,
          })),
        );
        setSites(list);
        setSiteId(list[0]?.id || null);
      } catch {
        msg.error("Failed to load sites");
      }
    })();
  }, [orgId]);

  useEffect(() => {
    if (!siteId) return;
    (async () => {
      try {
        const res = await getCrops(siteId);
        const list = (res?.data?.data || [])
          .filter((cr) => cr.is_active)
          .map((cr) => ({
            id: cr.id,
            name: cr.name,
            display_name: cr.display_name,
          }));
        setCrops(list);
        setCropId(list[0]?.id || null);
      } catch {
        msg.error("Failed to load crops");
      }
    })();
  }, [siteId]);

  useEffect(() => {
    if (!(siteId && cropId)) return;
    const siteObj = sites.find((s) => s.id === siteId);
    const cropObj = crops.find((c) => c.id === cropId);
    const metaForCrop = siteObj?.metadata
      ? siteObj.metadata[cropObj?.name]
      : undefined;

    const nextPlotLabel =
      metaForCrop?.block_name || siteObj?.metadata?.block_name || "Plot";
    const nextGhLabel =
      metaForCrop?.greenhouse_name ||
      siteObj?.metadata?.greenhouse_name ||
      "Greenhouse";

    setPlotHeaderLabel(nextPlotLabel);
    setGreenhouseHeaderLabel(nextGhLabel);
  }, [siteId, cropId]);

  /* greenhouses */
  useEffect(() => {
    if (!orgId || !siteId || !cropId) return;
    (async () => {
      try {
        const res = await getGreenhouses(orgId, siteId, cropId);
        const list = (res?.data?.data || []).map((g) => ({
          id: g.id,
          name: g.name,
        }));

        const map = new Map(list.map((g) => [g.id, g.name]));
        setGhMap(map);

        const sorted = sortGreenhousesSmart(list).map((g) => ({
          value: g.id,
          label: g.name,
        }));
        setGhOptions(sorted);

        // keep valid selections; otherwise default to **all** greenhouses
        const validSet = new Set(sorted.map((o) => o.value));
        const retained = selectedGreenhouses.filter((id) => validSet.has(id));
        if (retained.length) {
          setSelectedGreenhouses(retained);
        } else if (sorted.length) {
          setSelectedGreenhouses(sorted.map((o) => o.value));
        } else {
          setSelectedGreenhouses([]);
        }
      } catch {
        msg.error("Failed to load greenhouses");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, siteId, cropId]);

  /* allowed harvest dates */
  useEffect(() => {
    if (!orgId || !siteId || !cropId) return;
    (async () => {
      try {
        const res = await getHarvestDates(orgId, siteId, cropId);
        const dts = (res?.data?.data || []).map((h) => h.date).sort();
        setAllowedDates(dts);
        // keep selected dates; if none, pick latest
        setDates((prev) => {
          const filtered = prev.filter((d) => dts.includes(d)).sort();
          if (filtered.length) return filtered;
          const latest = dts[dts.length - 1];
          return latest ? [latest] : [];
        });
      } catch {
        msg.error("Failed to load harvest dates");
      }
    })();
  }, [orgId, siteId, cropId]);

  /* load table rows */
  const readyToQuery = useMemo(
    () =>
      Boolean(
        orgId &&
          siteId &&
          cropId &&
          selectedGreenhouses.length > 0 &&
          dates.length > 0 &&
          ghMap.size > 0,
      ),
    [
      orgId,
      siteId,
      cropId,
      selectedGreenhouses.length,
      dates.length,
      ghMap.size,
    ],
  );

  const loadTable = async () => {
    if (!readyToQuery) return { ok: false, count: 0 };
    setLoading(true);
    try {
      const res = await getPlotDataVisibility(
        orgId,
        siteId,
        cropId,
        selectedGreenhouses,
        dates,
      );
      const data = res?.data?.data || res?.data || [];

      let mapped = data.map((r) => {
        const ghId = resolveGreenhouseId(r);
        const ghName = resolveGreenhouseName(r, ghMap);
        const hvDate = resolveHarvestDate(r);
        const plotName = r.plot_name || r.plot?.name || "—";
        return {
          key: r.id,
          id: r.id,
          greenhouse_id: ghId,
          greenhouse_name: ghName,
          plot_name: plotName,
          harvest_date: hvDate,
          is_visible: r.is_visible,
        };
      });
      if (showHiddenOnly) mapped = mapped.filter((x) => !x.is_visible);

      mapped = sortRowsDefault(mapped);

      setRows(mapped);
      setDirty({});
      setSelectedRowKeys([]);
      return { ok: true, count: mapped.length };
    } catch {
      msg.error("Failed to load plot data visibility");
      return { ok: false, count: 0 };
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (readyToQuery) loadTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    readyToQuery,
    selectedGreenhouses.join(","),
    dates.join(","),
    showHiddenOnly,
  ]);

  /* handlers */
  const onToggleVisible = (id, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, is_visible: value } : r)),
    );
    setDirty((prev) => ({ ...prev, [id]: value }));
  };

  const bulkSet = (value) => {
    if (!selectedRowKeys.length) return;
    setRows((prev) =>
      prev.map((r) =>
        selectedRowKeys.includes(r.id) ? { ...r, is_visible: value } : r,
      ),
    );
    setDirty((prev) => {
      const next = { ...prev };
      selectedRowKeys.forEach((id) => (next[id] = value));
      return next;
    });
  };

  const saveChanges = async () => {
    if (!dirtyCount) {
      msg.info("No changes to save.");
      return;
    }
    const payload = Object.entries(dirty).map(([id, vis]) => ({
      id,
      is_visible: !!vis,
    }));

    msg.open({
      key: MSG_KEYS.save,
      type: "loading",
      content: `Saving ${payload.length} change(s)…`,
      duration: 0,
    });

    try {
      await updatePlotDataVisibility(payload);
      msg.open({
        key: MSG_KEYS.save,
        type: "success",
        content: `Saved ${payload.length} change(s) successfully.`,
      });
      setDirty({});
      await loadTable();
    } catch {
      msg.open({
        key: MSG_KEYS.save,
        type: "error",
        content: "Failed to save visibility changes.",
      });
    }
  };

  const addDate = (d) => {
    if (!d) return;
    const s = dayjs(d).format("YYYY-MM-DD");
    if (allowedSet.has(s)) {
      resetRows();
      setDates((prev) =>
        prev.includes(s) ? prev.slice().sort() : [...prev, s].sort(),
      );
    }
  };
  const removeDate = (s) => {
    resetRows();
    setDates((prev) => prev.filter((x) => x !== s).sort());
  };
  const disabledDate = (d) =>
    !d || !allowedSet.has(dayjs(d).format("YYYY-MM-DD"));

  const showAllHidden = () => {
    if (!ghOptions.length || !allowedDates.length) {
      msg.warning("Greenhouses and harvest dates must be available first.");
      return;
    }
    setPrevSelection({
      greenhouses: selectedGreenhouses.slice(),
      dates: dates.slice(),
    });
    resetRows();
    setSelectedGreenhouses(ghOptions.map((o) => o.value));
    setDates(allowedDates.slice()); // already sorted
    setShowHiddenOnly(true);
  };

  const clearHiddenView = () => {
    if (prevSelection) {
      setSelectedGreenhouses(prevSelection.greenhouses || []);
      setDates((prevSelection.dates || []).slice().sort());
    }
    setPrevSelection(null);
    setShowHiddenOnly(false);
    resetRows();
  };

  /* columns */
  const columns = [
    {
      title: greenhouseHeaderLabel,
      dataIndex: "greenhouse_name",
      key: "greenhouse_name",
      sorter: (a, b) =>
        String(a.greenhouse_name || "").localeCompare(
          String(b.greenhouse_name || ""),
        ),
      render: (text) => <Ellip text={text} />,
    },
    {
      title: plotHeaderLabel,
      dataIndex: "plot_name",
      key: "plot_name",
      sorter: (a, b) =>
        String(a.plot_name || "").localeCompare(String(b.plot_name || "")),
      render: (text) => <Ellip text={text} />,
    },
    {
      title: "Data Collection Date",
      dataIndex: "harvest_date",
      key: "harvest_date",
      sorter: (a, b) =>
        String(a.harvest_date || "").localeCompare(
          String(b.harvest_date || ""),
        ),
      render: (d) => (
        <Ellip
          text={d ? dayjs(d, BACKEND_DATE_FORMAT).format(UI_DATE_FORMAT) : "—"}
        />
      ),
    },
    {
      title: "Visible",
      dataIndex: "is_visible",
      key: "is_visible",
      width: 120,
      render: (_, record) => (
        <Switch
          checked={!!record.is_visible}
          checkedChildren={<EyeTwoTone />}
          unCheckedChildren={<EyeInvisibleOutlined />}
          onChange={(checked) => onToggleVisible(record.id, checked)}
        />
      ),
    },
  ];

  const tableScroll = useMemo(
    () => (rows.length ? { x: "max-content" } : undefined),
    [rows.length],
  );
  const selectionFixed = useMemo(
    () => (rows.length ? "left" : undefined),
    [rows.length],
  );

  const handleRefresh = async () => {
    msg.open({
      key: MSG_KEYS.refresh,
      type: "loading",
      content: "Refreshing data…",
      duration: 0,
    });
    const { ok, count } = await loadTable();
    if (ok) {
      msg.open({
        key: MSG_KEYS.refresh,
        type: "success",
        content: `Refreshed ${count} row${count === 1 ? "" : "s"}.`,
      });
    } else {
      msg.open({
        key: MSG_KEYS.refresh,
        type: "error",
        content: "Refresh failed.",
      });
    }
  };

  const allGhIds = useMemo(() => ghOptions.map((o) => o.value), [ghOptions]);
  const onSelectAllGreenhouses = () => {
    if (!allGhIds.length) return;
    resetRows();
    setSelectedGreenhouses(allGhIds);
    setShowHiddenOnly(false);
  };
  const onDeselectAllGreenhouses = () => {
    resetRows();
    setSelectedGreenhouses([]);
    setShowHiddenOnly(false);
  };

  /* ----- render ----- */
  return (
    <div className="h-full">
      {contextHolder}
      {/* Filters header */}
      <div className="w-full pb-4 h-fit flex items-start gap-3 lg:flex-col">
        <div className="flex flex-wrap items-center gap-3 flex-1 lg:w-full lg:flex-col">
          {userData?.is_superuser && (
            <Select
              className="flex-none basis-[220px] lg:basis-auto lg:w-full"
              placeholder="Organization"
              value={orgId}
              onChange={(v) => {
                resetForOrg(v);
              }}
              showSearch
              filterOption={(input, option) =>
                (option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={orgs.map((o) => ({ value: o.id, label: o.name }))}
              maxTagCount={1}
              maxTagTextLength={12}
            />
          )}

          <Select
            className="flex-none basis-[220px] lg:basis-auto lg:w-full"
            placeholder="Site"
            value={siteId}
            onChange={(v) => {
              resetForSite(v);
            }}
            showSearch
            filterOption={(input, option) =>
              (option?.label || "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={sites.map((s) => ({ value: s.id, label: s.name }))}
            maxTagCount={1}
            maxTagTextLength={12}
          />

          <Select
            className="flex-none basis-[220px] lg:basis-auto lg:w-full"
            placeholder="Crop"
            value={cropId}
            onChange={(v) => {
              resetForCrop(v);
            }}
            options={crops.map((c) => ({ value: c.id, label: c.name }))}
            showSearch
            filterOption={(input, opt) =>
              (opt?.label || "").toLowerCase().includes(input.toLowerCase())
            }
            maxTagCount={1}
            maxTagTextLength={12}
          />

          <Select
            mode="multiple"
            allowClear
            className="flex-none basis-[260px] lg:basis-auto lg:w-full"
            placeholder="Greenhouses"
            value={selectedGreenhouses}
            onChange={(v) => {
              resetRows();
              setSelectedGreenhouses(v);
              setShowHiddenOnly(false);
            }}
            options={ghOptions}
            maxTagCount={2}
            maxTagTextLength={10}
            popupRender={(menu) => (
              <div>
                <div className="px-2 py-2 flex gap-8">
                  <Button
                    size="small"
                    onClick={onSelectAllGreenhouses}
                    disabled={
                      !ghOptions.length ||
                      selectedGreenhouses.length === allGhIds.length
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    size="small"
                    onClick={onDeselectAllGreenhouses}
                    disabled={!selectedGreenhouses.length}
                  >
                    Deselect all
                  </Button>
                </div>
                <Divider style={{ margin: "4px 0" }} />
                {menu}
              </div>
            )}
          />

          <DatePicker
            className="flex-none basis-[180px] lg:basis-auto lg:w-full"
            value={datePickerValue}
            disabledDate={disabledDate}
            onChange={(v) => {
              if (v) setPrevSelection(null);
              addDate(v);
              setShowHiddenOnly(false);
            }}
            placeholder="Harvest date"
          />
          <Button
            onClick={showAllHidden}
            disabled={!ghOptions.length || !allowedDates.length}
          >
            View all hidden Data
          </Button>
        </div>

        <div className="flex flex-none gap-2 items-center ml-auto lg:hidden">
          <Tooltip title="Refresh">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              disabled={!readyToQuery}
            >
              Refresh
            </Button>
          </Tooltip>
          <Space wrap>
            <Button
              onClick={() => bulkSet(true)}
              disabled={!selectedRowKeys.length}
            >
              Set Visible
            </Button>
            <Button
              onClick={() => bulkSet(false)}
              disabled={!selectedRowKeys.length}
            >
              Set Hidden
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={saveChanges}
              disabled={!dirtyCount}
            >
              Save {dirtyCount ? `(${dirtyCount})` : ""}
            </Button>
          </Space>
        </div>
      </div>

      <div className="w-full mb-2">
        <div className="flex items-center gap-4">
          <div className="min-w-0 w-1/2 lg:w-full overflow-x-auto whitespace-nowrap pr-1">
            {dates.map((d) => (
              <Tag key={d} closable onClose={() => removeDate(d)}>
                {d}
              </Tag>
            ))}
          </div>

          {showHiddenOnly && (
            <Button
              size="small"
              onClick={clearHiddenView}
              className="lg:hidden"
            >
              Clear
            </Button>
          )}
        </div>

        {showHiddenOnly && (
          <div className="hidden lg:flex w-full justify-center mt-2">
            <Button size="small" onClick={clearHiddenView} className="w-40">
              Clear
            </Button>
          </div>
        )}
      </div>

      <div className="hidden lg:flex lg:flex-col lg:items-center lg:gap-2 lg:mt-2 w-full">
        <div className="w-full flex justify-center">
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            disabled={!readyToQuery}
            className="w-56"
          >
            Refresh
          </Button>
        </div>
        <div className="w-full flex justify-center">
          <Button
            onClick={() => bulkSet(true)}
            disabled={!selectedRowKeys.length}
            className="w-56"
          >
            Set Visible
          </Button>
        </div>
        <div className="w-full flex justify-center">
          <Button
            onClick={() => bulkSet(false)}
            disabled={!selectedRowKeys.length}
            className="w-56"
          >
            Set Hidden
          </Button>
        </div>
        <div className="w-full flex justify-center">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={saveChanges}
            disabled={!dirtyCount}
            className="w-56"
          >
            Save {dirtyCount ? `(${dirtyCount})` : ""}
          </Button>
        </div>
      </div>

      <Divider style={{ margin: "8px 0 16px" }} />

      <div
        className="w-full"
        style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
      >
        <Table
          loading={{
            spinning: loading,
            indicator: <LoadingIcon />,
          }}
          dataSource={rows}
          columns={columns}
          tableLayout="auto"
          scroll={tableScroll}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            fixed: selectionFixed,
            columnWidth: 48,
          }}
          pagination={{ hideOnSinglePage: true, position: ["bottomCenter"] }}
          size="middle"
        />
      </div>
    </div>
  );
};

export default PlotDataVisibilityTable;
